import { Lock } from "lucide-react";

import { Button } from "@/components/ui/button";

/**
 * Read-only notice shown when someone else holds the editor lease
 * (MDRS-554 / audit RB-8).
 *
 * The lease is advisory, and this banner is why: it tells the second arrival
 * that the report is already being edited BEFORE they spend ten minutes on a
 * draft they'd be asked to throw away. "Take over editing" is not an
 * afterthought — without it a crashed tab, a closed laptop or a dropped
 * network would lock a doctor out of their own report, which is a worse bug
 * than the overwrite being fixed.
 */
type Props = {
  /** Null = nothing to say; the banner renders nothing. */
  message: string | null;
  onTakeOver: () => Promise<void> | void;
  isTakingOver?: boolean;
};

export const EditorLeaseBanner = ({
  message,
  onTakeOver,
  isTakingOver = false,
}: Props) => {
  if (!message) return null;

  return (
    <div
      role="status"
      className="flex flex-wrap items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900"
    >
      <span className="flex items-center gap-2">
        <Lock className="size-4 shrink-0" aria-hidden />
        {message}
      </span>
      <Button
        variant="simple-outline"
        size="sm"
        onClick={() => void onTakeOver()}
        disabled={isTakingOver}
      >
        {isTakingOver ? "Taking over…" : "Take over editing"}
      </Button>
    </div>
  );
};
